import React, { useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';

export interface FreshserviceEntity {
  id: number;
  name: string;
}

interface FreshserviceEntityPickerProps {
  options: FreshserviceEntity[];
  value: number | null;
  onChange: (id: number | null) => void;
  label: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Name-search picker over a pre-fetched list of Freshservice entities
 * ({ id, name }). Stores the numeric id, displays the name. A saved id that
 * is not in the fetched list (stale or beyond the lookup cap) is shown as
 * "ID <n>" rather than blanking the field.
 */
const FreshserviceEntityPicker: React.FC<FreshserviceEntityPickerProps> = ({
  options,
  value,
  onChange,
  label,
  placeholder = 'Type to filter…',
  helperText,
  error,
  required,
  disabled,
  loading,
}) => {
  const effectiveOptions = useMemo(() => {
    if (value != null && !options.some((o) => o.id === value)) {
      return [{ id: value, name: `ID ${value}` }, ...options];
    }
    return options;
  }, [options, value]);

  const selected = effectiveOptions.find((o) => o.id === value) ?? null;

  return (
    <Autocomplete
      options={effectiveOptions}
      getOptionLabel={(o) => o.name}
      value={selected}
      onChange={(_, v) => onChange(v ? v.id : null)}
      disabled={disabled || loading}
      size="small"
      isOptionEqualToValue={(o, v) => o.id === v.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Typography variant="body2">{option.name}</Typography>
        </Box>
      )}
    />
  );
};

export default FreshserviceEntityPicker;
